import { useMemo } from 'react';
import { useQueryGetToastsData } from 'state/studio/toasts/getToastsData';
import { useQueryGetShopifyCollectionProducts } from 'processes/SubscriptionPageV2/hooks/productsByCollections';
import { useMutationUpdateSubscriptionLineItemV2 } from 'state/internal/updateSubscriptionLineItemV2';
import {
  SHOPIFY_AG1_POUCH_DOUBLE_SUB_VARIANT_ID,
  SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID
  // eslint-disable-next-line import/extensions
} from '@portal/config/base';
import { showNotification } from '../../shared/ui/Notification';
import { useCreatePerformanceTimer } from '../../shared/hooks/useCreatePerformanceTimer';
import { useStoreValue } from './useStore';

type Props = {
  contractId: string | number;
  singleVariantLineItemId: string;
  onSuccess?: (data: any) => void;
};

export const useDoubleSubscriptionRequestPromiseV2 = () => {
  const content = useQueryGetToastsData().data?.useAddProduct;
  const createPerformanceTimer = useCreatePerformanceTimer();

  const updateLineItemMutation = useMutationUpdateSubscriptionLineItemV2();
  const store = useStoreValue();
  const { data: collectionProducts } = useQueryGetShopifyCollectionProducts({
    storeCode: store,
    collectionHandle: 'bab'
  });

  const singleSubVariantId = SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID[store].default;
  const doubleSubVariantId = SHOPIFY_AG1_POUCH_DOUBLE_SUB_VARIANT_ID[store].default;

  const doubleSubscriptionItem = useMemo(
    () =>
      collectionProducts?.find((product) =>
        product.variants.find((variant) => variant.id === doubleSubVariantId)
      ),
    [collectionProducts, doubleSubVariantId]
  );

  const doubleSubscriptionRequestPromiseV2 = ({
    contractId,
    singleVariantLineItemId,
    onSuccess
  }: Props) => {
    if (!singleSubVariantId || !doubleSubVariantId || !collectionProducts?.length) {
      throw new Error(content?.errorMessage);
    }

    const timer = createPerformanceTimer('membersSubscriptionAddProductViaModalWindow', {
      membersSubscriptionAddProductMode: 'nextOrder',
      membersSubscriptionProductId: doubleSubscriptionItem?.id,
      membersSubscriptionProductTitle: doubleSubscriptionItem?.title,
      membersSubscriptionProductVariantId: doubleSubscriptionItem?.variants[0].id
    });

    // add double subscription variant (quantity 2) to subscription
    return updateLineItemMutation.mutateAsync(
      {
        contractId,
        lineItemId: singleVariantLineItemId,
        data: {
          shopify_product_variant_id: `gid://shopify/ProductVariant/${doubleSubVariantId}`,
          quantity: 2
        }
      },
      {
        onSuccess: (data: any) => {
          timer.success();
          if (onSuccess) {
            onSuccess(data);
          } else {
            showNotification('mutationSuccess', {
              title: content?.addedToSubscription
            });
          }
        },
        onError: (error: any) => {
          timer.failure(error);

          showNotification('mutationError');
        }
      }
    );
  };

  return {
    doubleSubscriptionRequestPromiseV2,
    isPending: updateLineItemMutation.isPending
  };
};
